import { Dispatch, FC, SetStateAction, Suspense, useEffect, useState } from 'react';
import { IndividualStudentRoadmapOverall } from './IndividualStudentRoadmapOverall';
import { IndividualStudentHeader } from './IndividualStudentHeader';
import {
  IndividualStudentReportRoadmapOverallQuery,
  IndividualStudentReportRoadmapOverallQuery$variables,
} from '../../graphQLOperations/__generated__/IndividualStudentReportRoadmapOverallQuery.graphql';
import { PreloadedQuery, useLazyLoadQuery, UseQueryLoaderLoadQueryOptions } from 'react-relay';
import {
  IndividualStudentReportOverallQuery,
  IndividualStudentReportOverallQuery$variables,
} from '../../graphQLOperations/__generated__/IndividualStudentReportOverallQuery.graphql';
import { IndividualStudentProjection } from './IndividualStudentProjection';
import { IndividualReportProjectionMsg } from './IndividualReportProjectionMsg';
import { getDataFromIndividualReport } from '../../graphQLOperations/IndividualStudentReport';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { IndividualStudentReportDataQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportDataQuery.graphql';
import { IndividualReportAssessments } from './IndividualReportAssessments';
import {
  IndividualStudentReportStatsQuery,
  IndividualStudentReportStatsQuery$variables,
} from '../../graphQLOperations/__generated__/IndividualStudentReportStatsQuery.graphql';
import { IndividualReportSkillLearningActivity } from './IndividualReportSkillLearningActivity';
import { useParams } from 'react-router-dom';
import { SkillsPresignedURLsQuery$variables } from '../../graphQLOperations/__generated__/SkillsPresignedURLsQuery.graphql';
import { IndividualReportUnavailable } from './IndividualReportUnavailable';

interface Props {
  id: string;
  setIndividualStudentId: Dispatch<SetStateAction<string | null>>;
  queryReferenceWeekly: PreloadedQuery<IndividualStudentReportStatsQuery, Record<string, unknown>> | null | undefined;
  loadWeeklyStats: (
    variables: IndividualStudentReportStatsQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions,
  ) => void;
  queryReferenceRoadmap:
    | PreloadedQuery<IndividualStudentReportRoadmapOverallQuery, Record<string, unknown>>
    | null
    | undefined;
  loadRoadmapOverall: (
    variables: IndividualStudentReportRoadmapOverallQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions,
  ) => void;
  queryReferenceProjection:
    | PreloadedQuery<IndividualStudentReportOverallQuery, Record<string, unknown>>
    | null
    | undefined;
  loadPresignedUrl: (variables: SkillsPresignedURLsQuery$variables, options?: UseQueryLoaderLoadQueryOptions) => void;
  loadOverallProjection: (
    variables: IndividualStudentReportOverallQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions,
  ) => void;
  setSelectedModalSkill: Dispatch<SetStateAction<{ code: string } | null>>;
  selectedModalSkill: { code: string } | null;
}

export interface SelectedSkillIndividualReport {
  skillId: string;
  lastSession: string | null;
}
export interface StudentInfoInterface {
  firstName: string | null | undefined;
  id: string;
  lastName: string | null | undefined;
  publicUserId: string | null | undefined;
  userClasses:
    | readonly (
        | {
            class:
              | {
                  id: string;
                  name: string | null | undefined;
                  section: string | null | undefined;
                }
              | null
              | undefined;
            id: string;
            isActive: boolean;
          }
        | null
        | undefined
      )[]
    | null
    | undefined;
  sites:
    | readonly (
        | {
            readonly id: string;
          }
        | null
        | undefined
      )[]
    | null
    | undefined;
}

export const IndividualReportWrapper: FC<Props> = ({
  id,
  setIndividualStudentId,
  queryReferenceRoadmap,
  loadRoadmapOverall,
  loadOverallProjection,
  queryReferenceProjection,
  queryReferenceWeekly,
  loadWeeklyStats,
  setSelectedModalSkill,
  selectedModalSkill,
  loadPresignedUrl,
}) => {
  const [selectedSkill, setSelectedSkill] = useState<SelectedSkillIndividualReport | null>(null);
  const { cids } = useParams();
  const getClassIds = () => {
    if (cids) {
      return cids.split('-').map(cid => `ClassNode:${cid}`);
    }
    return [];
  };

  const {
    latestCompletedStudentSkillDiagnostic,
    currentTargetGroupByStudentId,
    allStudents,
    studentById: studentInfo,
  } = useLazyLoadQuery<IndividualStudentReportDataQuery>(
    getDataFromIndividualReport,
    {
      skip: false,
      studentId: id,
      id: id,
      filter: {
        classIds: getClassIds(),
      },
    },
    { fetchKey: id, fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (selectedModalSkill) {
      loadPresignedUrl({ skillCode: selectedModalSkill?.code || '', filter: { storedObjectType: 'ALL' } });
    }
  }, [selectedModalSkill]);

  if (!currentTargetGroupByStudentId?.targetGroup) {
    return <IndividualReportUnavailable />;
  }

  return (
    <div className="tw-flex tw-justify-center">
      {selectedSkill ? (
        <IndividualReportAssessments
          id={id}
          selectedSkill={selectedSkill}
          setSelectedSkill={setSelectedSkill}
          setIndividualStudentId={setIndividualStudentId}
          studentInfo={studentInfo}
        />
      ) : (
        <div className="nc_individual-report-container tw-w-[85%] tw-mb-[5rem]">
          <Suspense fallback={<ThreeDotLoader dark />}>
            <IndividualStudentHeader
              setIndividualStudentId={setIndividualStudentId}
              studentInfo={studentInfo}
              id={id}
              allStudents={allStudents}
            />
          </Suspense>
          <div className="tw-p-8 tw-border tw-border-solid tw-rounded-3xl tw-border-[#D3D3D3] tw-bg-white">
            <Suspense fallback={<ThreeDotLoader dark />}>
              <IndividualStudentProjection
                diagnostic={latestCompletedStudentSkillDiagnostic}
                id={id}
                queryReferenceProjection={queryReferenceProjection}
                loadOverallProjection={loadOverallProjection}
              />
            </Suspense>
            <div className="tw-mt-4 tw-block tw-w-full tw-rounded-lg tw-bg-neutral-0 tw-py-5">
              {!latestCompletedStudentSkillDiagnostic?.retakeAssigned && latestCompletedStudentSkillDiagnostic && (
                <Suspense fallback={<ThreeDotLoader dark />}>
                  <IndividualStudentRoadmapOverall
                    setSelectedSkill={setSelectedSkill}
                    id={id}
                    queryReferenceRoadmap={queryReferenceRoadmap!}
                    loadRoadmapOverall={loadRoadmapOverall}
                    currentRoadmap={currentTargetGroupByStudentId.targetGroup}
                  />
                </Suspense>
              )}
            </div>
            <div className="tw-p-4 tw-bg-[#F9F8F5] mt-4 tw-rounded-2xl">
              {queryReferenceProjection &&
                !latestCompletedStudentSkillDiagnostic?.retakeAssigned &&
                latestCompletedStudentSkillDiagnostic && (
                  <Suspense fallback={<ThreeDotLoader dark />}>
                    <IndividualReportProjectionMsg
                      id={id}
                      currentRoadmap={currentTargetGroupByStudentId.targetGroup}
                      studentFirstName={studentInfo?.firstName ?? ''}
                      publicUserId={studentInfo?.publicUserId ?? ''}
                      queryReferenceProjection={queryReferenceProjection}
                    />
                  </Suspense>
                )}
            </div>
            <div className="tw-mt-8">
              <IndividualReportSkillLearningActivity
                siteId={studentInfo?.sites?.length ? studentInfo.sites[0]?.id : null}
                setSelectedModalSkill={setSelectedModalSkill}
                weeklyStatsRef={queryReferenceWeekly}
                loadWeeklyStats={loadWeeklyStats}
                studentId={id}
                setSelectedSkill={setSelectedSkill}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
